import { JobTypes } from '@cdw-ae/common-data';

/**
 * UI shape of a bulk action.
 *
 * - `field`: legacy bulk-edit path (host renders the field-selection step,
 *   summary table is built from field updates).
 * - `status`: defaults-driven status change (no field-selection step; summary
 *   table is built from `defaultValues`). Confirm step gates execution.
 * - `command`: async device command (no field-selection step, no summary
 *   table); executor injects `command` per row.
 */
export type BulkActionKind = 'field' | 'status' | 'command';

/**
 * Confirm-step UI shape. Drives the template fragment rendered before the
 * Summary step.
 *
 * - `'prose'`                — informational text only (e.g. Enable, Disable).
 * - `'warning'`              — warning band + prose, no checkbox (e.g. Profile Wipe).
 * - `'warning-acknowledge'`  — warning band + prose + acknowledge checkbox (e.g. Factory Reset).
 * - `'none'`                 — no Confirm step rendered (field-type actions, deprovision).
 */
export type ConfirmArchetype = 'prose' | 'warning' | 'warning-acknowledge' | 'none';

export interface ActionLabels {
  /** Drawer toolbar title (e.g. "Bulk Enable Devices"). */
  drawerTitle: string;
  /**
   * Ordered stepper labels. Length drives the derived Progress / Completed
   * step indices (`progressIndex = steps.length - 2`,
   * `completedIndex = steps.length - 1`). Eliminates magic numbers in the
   * component.
   */
  steps: string[];
  /** Title shown above the progress bar on the Progress step. */
  progressTitle: string;
  /**
   * Summary "pending" line template. `{Y}` substitutes the qualified count.
   * Whether the line is shown is gated on `eligibility.totals.qualified > 0`
   * — when zero we render the degenerate "no devices eligible" warning band
   * instead.
   */
  summaryPendingTemplate: string;
  /**
   * Completed step success line template. `{Y}` substitutes the count of
   * successfully-acted-on items.
   */
  completedTitleTemplate: string;
  /** Completed step skipped line template. `{Z}` substitutes the skipped count. */
  completedSkippedTemplate: string;

  /**
   * UI shape of this action. The drawer derives `isStatusOnlyAction` and
   * `isCommandOnlyAction` from this rather than enumerating action-type
   * strings. Defaults to `'field'` when omitted (legacy bulk-edit path).
   */
  kind?: BulkActionKind;
  /**
   * Backend job-type token passed in the executor request. Eliminates the
   * drawer's hard-coded `JobTypes.WIPE_USERS` etc. switch.
   */
  jobType?: string;
  /**
   * Per-row device-command token for `kind: 'command'` actions (e.g.
   * `'WIPE_USERS'`, `'REMOTE_POWERWASH'`). Ignored for other kinds.
   */
  command?: string;
  /**
   * Confirm-step archetype. Defaults to `'none'` when omitted (no Confirm
   * step rendered — legacy bulk-edit path).
   */
  confirmArchetype?: ConfirmArchetype;
  /**
   * Inner HTML for the warning band when `confirmArchetype` is `'warning'` or
   * `'warning-acknowledge'`. `{Y}` substitutes the selected count. Use
   * `<strong>` etc. for inline emphasis. Ignored for `'prose'` / `'none'`.
   */
  warningBandCopy?: string;
  /**
   * Plain prose under the warning band (or as the only content for `'prose'`
   * archetype). `{Y}` substitutes the selected count.
   */
  confirmProseCopy?: string;
  /**
   * Label next to the acknowledge checkbox when `confirmArchetype` is
   * `'warning-acknowledge'`. Ignored for other archetypes.
   */
  acknowledgeLabel?: string;
  /**
   * Label for the primary action button on the Summary step (e.g. "Disable",
   * "Enable", "Queue Wipe", "Move OU"). Defaults to "Execute" when omitted.
   */
  summaryActionButtonLabel?: string;
}

export interface BulkActionLabelsProvider {
  getLabels(actionType: string | undefined): ActionLabels;
  /** Optional template formatter. The drawer falls back to `{Y}`/`{Z}` substitution when omitted. */
  formatLabel?(template: string, counts: { qualified?: number; skipped?: number }): string;
}

/**
 * Default labels used when no `actionType` is supplied or when the host
 * provider doesn't recognize the actionType. Preserves the existing 4-step
 * generic bulk-edit copy.
 */
export const DEFAULT_ACTION_LABELS: ActionLabels = {
  drawerTitle: 'Bulk Edit',
  steps: ['Field Selection', 'Summary', 'Progress', 'Completed'],
  progressTitle: 'Bulk edit in progress',
  summaryPendingTemplate:
    'This action will update {Y} device(s) with the following changes.',
  completedTitleTemplate: 'Bulk edit completed successfully for {Y} item(s)',
  completedSkippedTemplate: '{Z} device(s) were skipped. See more info',
  kind: 'field',
  jobType: JobTypes.BULK_EDIT,
  confirmArchetype: 'none',
};
